import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  getDynamicPTSProjects, createDynamicPTSProject,
  getKategoriat, getKuntoarviovalikot, createKuntoarviovalikko,
  getTarkastuskohteetV2, createTarkastuskohdeV2, deleteTarkastuskohdeV2, createSectionForTarkastuskohdeV2,
  getImageSubcategories,
} from '../../../../../api/api';
import { uniqueProjectName } from '../../../../../utils/subsectionUtils';

// Report types that always need their own PTS project — ported from v1's
// SubsectionBuilderBlock.jsx AUTO_PTS_REPORT_TYPES/defaultProjectName/
// REPORT_TYPE_LABELS, so a fresh julkisivu (etc.) report gets one
// auto-created and linked without the user having to click "+ Luo uusi".
// Includes the "_v2" report type variants used while julkisivu v2 blocks
// are being tested (reports.type = 'julkisivujen_kuntotutkimus_v2' etc.,
// distinct from v1's 'julkisivujen_kuntotutkimus') — confirmed both exist
// side by side in the DB.
const AUTO_PTS_REPORT_TYPES = new Set([
  'julkisivujen_kuntotutkimus', 'julkisivujen_kuntotutkimus_v2',
  'ryomintatilojen_kuntotutkimus', 'ryomintatilojen_kuntotutkimus_v2',
  'vesikaton_kuntotutkimus', 'vesikaton_kuntotutkimus_v2',
  'ikkuna_ovi_raportti', 'ikkunoiden_ja_ulko-ovien_kuntoarvio', 'ikkunoiden_ja_ulko-ovien_kuntoarvio_v2',
]);
const REPORT_TYPE_LABELS = {
  julkisivujen_kuntotutkimus: 'Julkisivujen kuntotutkimus',
  julkisivujen_kuntotutkimus_v2: 'Julkisivujen kuntotutkimus',
  ikkuna_ovi_raportti: 'Ikkuna- ja oviraportit',
  'ikkunoiden_ja_ulko-ovien_kuntoarvio': 'Ikkuna- ja oviraportit',
  'ikkunoiden_ja_ulko-ovien_kuntoarvio_v2': 'Ikkuna- ja oviraportit',
  kuntoarvio: 'Kuntoarvio',
  kuntoraportti: 'Kuntoraportti',
  vesikaton_kuntotutkimus: 'Vesikaton kuntotutkimus',
  vesikaton_kuntotutkimus_v2: 'Vesikaton kuntotutkimus',
  ryomintatilojen_kuntotutkimus: 'Ryömintätilojen kuntotutkimus',
  ryomintatilojen_kuntotutkimus_v2: 'Ryömintätilojen kuntotutkimus',
  lvi_raportti: 'LVI-kuntoarvio',
};
// Exported so other "create a PTS project" entry points (e.g. the
// standalone kuntoarvio workspace's own "+ Uusi projekti", which has no
// reportType of its own to go on) can suggest a name with the same
// convention, rather than inventing a second one.
export function defaultProjectName(reportType) {
  const label = REPORT_TYPE_LABELS[reportType] ?? (reportType ?? 'Projekti').replace(/_/g, ' ');
  return `${label} ${new Date().toLocaleDateString('fi-FI')}`;
}

// Service layer for KuntoarviovalikkoView, matching how the real
// SubsectionBuilderBlock actually behaves: one PTS-projekti dropdown, then
// every category shown at once, each offering one-click "ready to use"
// suggestion chips (static list + real historical names pulled live from
// image_subcategories, same data v1's chips are sourced from) plus a
// custom-name field. A kuntoarviovalikko row for a category is created
// lazily, on the first tarkastuskohde added to it — mirrors v1 creating its
// section lazily on first use rather than up front. Passing reportId makes
// each add also create+order the real report_sections row on the backend
// (cloned from the category's DB template, same mechanism v1 uses) —
// fetchReportData refreshes the editor afterwards so it appears immediately.
// `standalone`: true when this hook is used OUTSIDE a report (no reportId
// ever exists, by design — e.g. the taloyhtiökortti kuntoarvio workspace).
// createTarkastuskohdeV2 always responds the same shape whether or not a
// report section got cloned; without a report, `section` is legitimately
// always null, which the report-embedded path below treats as an ERROR
// (`section_skipped_reason`) since there it really would mean something
// went wrong. Standalone, "no report to attach a section to" is the
// expected, normal outcome — surface it as success instead.
// `sections` (report-embedded only): the report's own section list
// (ReportTemplateContext's `sections`, straight off `report.report_sections`)
// — used only to detect tarkastuskohde that have no matching section yet
// (e.g. added from the standalone workspace, which never clones one).
// Matching follows the same convention v1's SubsectionBuilderBlock chip
// path actually uses (type === 'section_chip_' + category.toLowerCase(),
// name === tarkastuskohde.nimi) — not a hard FK, since the backend
// doesn't expose one to the client today.
export default function useKuntoarviovalikkoV2(id_kiinteisto, value, onChange, reportId, fetchReportData, reportType, standalone = false, sections = []) {
  const ptsProjectId = value?.ptsProjectId ?? null;

  const [ptsProjects, setPtsProjects] = useState([]);
  const [ptsLoaded, setPtsLoaded] = useState(false);
  const autoPtsAttempted = useRef(false);
  const [kategoriat, setKategoriat] = useState([]);
  const [kavByCategory, setKavByCategory] = useState({});
  const [tkByCategory, setTkByCategory] = useState({});
  const [suggestionsByCategory, setSuggestionsByCategory] = useState({});
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => { getKategoriat().then(r => setKategoriat(r.data)).catch(() => {}); }, []);

  const refreshPtsProjects = useCallback(() => {
    if (!id_kiinteisto) { setPtsProjects([]); setPtsLoaded(true); return; }
    getDynamicPTSProjects(id_kiinteisto).then(r => setPtsProjects(r.data || [])).catch(() => {}).finally(() => setPtsLoaded(true));
  }, [id_kiinteisto]);
  useEffect(refreshPtsProjects, [refreshPtsProjects]);

  // Auto-create + link a PTS project the first time this block has none
  // chosen yet, for report types that always need one — ported from v1's
  // SubsectionBuilderBlock (AUTO_PTS_REPORT_TYPES), so a fresh julkisivu
  // report never requires the user to manually click "+ Luo uusi" first.
  // One-shot per mount (ref guard), same as v1's effect firing once per
  // id_kiinteisto rather than reactively on every ptsProjectId change.
  useEffect(() => {
    if (!ptsLoaded || autoPtsAttempted.current) return;
    if (ptsProjectId || !id_kiinteisto || !reportType || !AUTO_PTS_REPORT_TYPES.has(reportType)) return;
    autoPtsAttempted.current = true;
    const name = uniqueProjectName(defaultProjectName(reportType), ptsProjects);
    createDynamicPTSProject(id_kiinteisto, name, new Date().getFullYear())
      .then(r => {
        setPtsProjects(prev => [...prev, r.data]);
        onChange({ ...value, ptsProjectId: r.data.id });
      })
      .catch(() => {});
  }, [ptsLoaded, ptsProjectId, id_kiinteisto, reportType, ptsProjects, onChange, value]);

  const pickPtsProject = useCallback((id) => {
    onChange({ ...value, ptsProjectId: id ? Number(id) : null });
  }, [onChange, value]);

  const createPtsProject = useCallback(async (name) => {
    if (!id_kiinteisto || !name.trim()) return;
    setError('');
    try {
      const r = await createDynamicPTSProject(id_kiinteisto, name.trim(), new Date().getFullYear());
      refreshPtsProjects();
      onChange({ ...value, ptsProjectId: r.data.id });
    } catch (e) { setError(e?.response?.data?.error || e.message); }
  }, [id_kiinteisto, onChange, value, refreshPtsProjects]);

  const refreshCategoryData = useCallback(async () => {
    if (!ptsProjectId || !kategoriat.length) return;
    const kavList = (await getKuntoarviovalikot(ptsProjectId).catch(() => ({ data: [] }))).data;
    const kavMap = {};
    kategoriat.forEach(cat => { kavMap[cat.value] = kavList.find(k => k.category === cat.value) || null; });
    setKavByCategory(kavMap);

    const tkEntries = await Promise.all(kategoriat.map(async cat => {
      const kav = kavMap[cat.value];
      if (!kav) return [cat.value, []];
      const r = await getTarkastuskohteetV2(kav.id).catch(() => ({ data: [] }));
      return [cat.value, r.data];
    }));
    setTkByCategory(Object.fromEntries(tkEntries));

    const sugEntries = await Promise.all(kategoriat.map(async cat => {
      const fetched = await getImageSubcategories(cat.imageCategory).catch(() => ({ data: [] }));
      const historical = (fetched.data || []).map(r => r.subcategory).filter(Boolean);
      const merged = [...new Set([...(cat.suggestions || []), ...historical])];
      return [cat.value, merged];
    }));
    setSuggestionsByCategory(Object.fromEntries(sugEntries));
  }, [ptsProjectId, kategoriat]);
  useEffect(() => { refreshCategoryData(); }, [refreshCategoryData]);

  // Returns the created tarkastuskohde ({id, nimi, ...}) on success, or null
  // — lets a standalone caller (no report section to fall back on for "what
  // did we just make") auto-select the thing it just created.
  const addTarkastuskohde = useCallback(async (category, nimi) => {
    const trimmed = nimi.trim();
    if (!trimmed || !ptsProjectId) return null;
    setError('');
    setStatus('');
    setSavingKey(category + ':' + trimmed);
    try {
      let kav = kavByCategory[category];
      if (!kav) {
        const r = await createKuntoarviovalikko(ptsProjectId, category);
        kav = r.data.kuntoarviovalikko;
      }
      // Response is always the same shape regardless of what happened server-side
      // — {tarkastuskohde, section, section_order, section_skipped_reason} — so
      // we read section_skipped_reason instead of inferring anything from reportId.
      const { data } = await createTarkastuskohdeV2(kav.id, trimmed, reportId);
      await refreshCategoryData();
      if (data.section) {
        await fetchReportData?.();
        setStatus(`✓ Osio luotu ja lisätty raporttiin: "${data.section.name}" (id ${data.section.id}, tyyppi ${data.section.type})`);
      } else if (standalone) {
        setStatus(`✓ Tarkastuskohde "${data.tarkastuskohde.nimi}" luotu.`);
      } else {
        setError(`Osiota ei lisätty raporttiin: ${data.section_skipped_reason ?? 'tuntematon syy'}. Tarkastuskohde (id ${data.tarkastuskohde.id}) luotiin silti.`);
      }
      setSavingKey(null);
      return data.tarkastuskohde;
    } catch (e) {
      const apiError = e?.response?.data;
      setError(apiError ? `${apiError.error}${apiError.got ? ' — got: ' + JSON.stringify(apiError.got) : ''} (HTTP ${e.response.status})` : e.message);
      setSavingKey(null);
      return null;
    }
  }, [ptsProjectId, kavByCategory, refreshCategoryData, reportId, fetchReportData, standalone]);

  // Only meaningful report-embedded (reportId set) — standalone has no
  // report_sections to compare against, and `sections` is always `[]`
  // there, so this naturally comes out empty.
  const missingSections = useMemo(() => (reportId ? kategoriat.flatMap(cat => {
    const chipType = 'section_chip_' + cat.value.toLowerCase();
    return (tkByCategory[cat.value] || [])
      .filter(tk => !sections.some(s => s.type === chipType && s.name === tk.nimi))
      .map(tk => ({ category: cat.value, tk }));
  }) : []), [reportId, kategoriat, tkByCategory, sections]);

  // Creates ONLY the report_section for an existing tarkastuskohde — never
  // touches tarkastuskohde data (confirmed by testing: re-posting an
  // existing name through createTarkastuskohdeV2 just inserts a second
  // tarkastuskohde row instead of attaching a section to the first one,
  // since tarkastuskohde and report section are different things — see
  // createSectionForTarkastuskohdeV2's own comment in api.js).
  const [creatingMissing, setCreatingMissing] = useState(false);
  const createMissingSections = useCallback(async () => {
    if (!missingSections.length || creatingMissing) return;
    setCreatingMissing(true);
    setError('');
    try {
      for (const { tk } of missingSections) {
        await createSectionForTarkastuskohdeV2(tk.id, reportId);
      }
      await refreshCategoryData();
      await fetchReportData?.();
      setStatus(`✓ ${missingSections.length} puuttuvaa osiota luotu.`);
    } catch (e) {
      if (e?.response?.status === 404) {
        setError('Backend-reitti osion luontiin olemassa olevalle tarkastuskohteelle puuttuu vielä (POST /api/julkisivu/tarkastuskohteet/:id/create_section) — ei vielä käytettävissä.');
      } else {
        const apiError = e?.response?.data;
        setError(apiError?.error ? `${apiError.error} (HTTP ${e.response.status})` : e.message);
      }
    } finally {
      setCreatingMissing(false);
    }
  }, [missingSections, creatingMissing, reportId, refreshCategoryData, fetchReportData]);

  // Lets the user remove a mistakenly-duplicated tarkastuskohde (e.g. one
  // created by re-posting an existing name — see missingSections above) —
  // nothing in this view could do this before.
  const deleteTarkastuskohde = useCallback(async (tk) => {
    try { await deleteTarkastuskohdeV2(tk.id); await refreshCategoryData(); await fetchReportData?.(); }
    catch (e) { setError(e?.response?.data?.error || e.message); }
  }, [refreshCategoryData, fetchReportData]);

  return {
    ptsProjects, ptsLoaded, ptsProjectId, pickPtsProject, createPtsProject,
    kategoriat, tkByCategory, suggestionsByCategory, error, status, savingKey,
    addTarkastuskohde, missingSections, creatingMissing, createMissingSections, deleteTarkastuskohde,
  };
}
